import { useCallback, useMemo, useState } from 'react'

import { findDynamicSectionByPath } from '@services/sections'
import { useRouter } from 'next/router'

import { RolrSetupContext } from './context'
import {
  computeNextState,
  getRolrStepRoute,
  computeRolrStateMachineNextState,
  findStepBasedOnRoute,
} from './utils'

import type { RolrSetupContextActions, RolrSetupContextState } from './context'
import type { RolrStepStatuses } from './utils'

export interface RolrSetupProviderProps {
  /**
   * Used mainly for testing.
   */
  initialState?: RolrSetupContextState
  /**
   * React Node Children.
   */
  children?: React.ReactNode
}

export const RolrSetupProvider: React.FC<RolrSetupProviderProps> = ({
  children,
  initialState,
}) => {
  const router = useRouter()

  const [state, setState] = useState<RolrSetupContextState>({
    step: 'landing',
    ...initialState,
  })

  const [stepStatus, setStepStatus] = useState<RolrStepStatuses>(() =>
    computeRolrStateMachineNextState(state),
  )

  const mergeState = useCallback((newState: Partial<RolrSetupContextState>) => {
    setState((curr) => ({
      ...curr,
      ...newState,
    }))
  }, [])

  /**
   * Compute next state based on a given step.
   */
  const computeNextStep = useCallback<
    RolrSetupContextActions['computeNextStep']
  >(
    (action) => {
      const nextState = computeNextState(state, action)

      if (!nextState) {
        return
      }

      const nextRoute = getRolrStepRoute(nextState.step)
      const nextStepStatus = computeRolrStateMachineNextState(nextState)

      setState(nextState)
      setStepStatus(nextStepStatus)

      if (!nextRoute || !nextStepStatus[nextState.step].allDependenciesValid) {
        return
      }

      void router.push(nextRoute)
    },
    [state, router],
  )

  /**
   * Determine if step is valid.
   */
  const isStepValid = useCallback<RolrSetupContextActions['isStepValid']>(
    (step) => {
      return Boolean(stepStatus[step]?.isValid)
    },
    [stepStatus],
  )

  const areAllDependenciesValidForStep = useCallback<
    RolrSetupContextActions['areAllDependenciesValidForStep']
  >(
    (step) => {
      return Boolean(stepStatus[step]?.allDependenciesValid)
    },
    [stepStatus],
  )

  const goToStep = useCallback<RolrSetupContextActions['goToStep']>(
    (step) => {
      if (!stepStatus[step]?.allDependenciesValid) {
        return
      }

      const route = getRolrStepRoute(step)

      mergeState({
        step,
      })

      void router.push(route)
    },
    [mergeState, stepStatus, router],
  )

  const validateStepDependencies = useCallback<
    RolrSetupContextActions['validateStepDependencies']
  >(
    (route) => {
      const foundSection = findDynamicSectionByPath(route)

      if (!foundSection) {
        return
      }

      const desiredStep = findStepBasedOnRoute(foundSection.route)

      // user is visiting a step not defined in this flow, let middleware permissions handle this case, no need to redirect
      if (!desiredStep) {
        return
      }

      // user is already on the step they should be on
      if (desiredStep === state.step) {
        return
      }

      const status = stepStatus[desiredStep]

      if (Array.from(Object.keys(stepStatus)).length === 0 || status == null) {
        // we have no status yet, always route to first step, should not be possible but to cover all grounds
        mergeState({
          step: 'landing',
        })
        void router.replace(getRolrStepRoute('landing'))
      }

      const { allDependenciesValid, firstInvalidDependency } = status

      if (allDependenciesValid) {
        mergeState({
          step: desiredStep,
        })
        return
      }

      if (firstInvalidDependency) {
        mergeState({
          step: firstInvalidDependency,
        })
        void router.replace(getRolrStepRoute(firstInvalidDependency))
      }
    },
    [state.step, stepStatus, mergeState, router],
  )

  const value = useMemo<[RolrSetupContextState, RolrSetupContextActions]>(
    () => [
      state,
      {
        computeNextStep,
        isStepValid,
        goToStep,
        validateStepDependencies,
        areAllDependenciesValidForStep,
      },
    ],
    [
      state,
      computeNextStep,
      isStepValid,
      goToStep,
      validateStepDependencies,
      areAllDependenciesValidForStep,
    ],
  )

  return (
    <RolrSetupContext.Provider value={value}>
      {children}
    </RolrSetupContext.Provider>
  )
}
